// src/pages/retail-shops/RetailSalesDashboard.jsx
import React, {
  useState, useEffect, useCallback, useRef, useMemo, memo, Suspense,
} from 'react';
import axios from 'axios';

// ── MUI Core ──
import {
  Box, Typography, Grid, Card, CardContent, Chip, IconButton,
  TextField, InputAdornment, Alert, Snackbar, Fade, Avatar,
  useTheme, alpha, CircularProgress, Skeleton, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip, Stack, Divider, useMediaQuery,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Button, MenuItem, Select, FormControl,
  InputLabel, Tabs, Tab,
} from '@mui/material';

// ── MUI Icons ──
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  DateRange as DateRangeIcon,
  Close as CloseIcon,
  AttachMoney as AttachMoneyIcon,
  ReceiptLong as ReceiptLongIcon,
  Today as TodayIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as ShowChartIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  ShoppingBag as ShoppingBagIcon,
  AccountBalanceWallet as WalletIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

// ── Chart.js ──
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip as ChartTooltip, Legend, ArcElement,
  PointElement, LineElement, Filler, RadialLinearScale,
  PolarAreaController,
} from 'chart.js';
import { Bar, Line, PolarArea, Radar } from 'react-chartjs-2';

// ── Layout ──
import '../../styles/Waiters.css';
import Sidebar from '../../components/tuck_shop/Sidebar';
import Header from '../../components/tuck_shop/Header';
import Footer from '../../components/tuck_shop/Footer';
import { ACCE_BRAND } from '../../theme/colors';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend,
  ArcElement, PointElement, LineElement, Filler, RadialLinearScale,
  PolarAreaController
);

// ════════════════════════════════════════════
// API
// ════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const api = axios.create({
  baseURL: FULL_API_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const extractError = (err) => {
  const d = err?.response?.data;
  if (!d) return err.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (d.errors) {
    const first = Object.values(d.errors)[0];
    return Array.isArray(first) ? first[0] : String(first);
  }
  return d.message || 'Request failed';
};

const apiListSales = async (params = {}) => {
  try {
    const { data } = await api.get('/tuckshop/sales', { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiGetSale = async (id) => {
  try {
    const { data } = await api.get(`/tuckshop/sales/${id}`);
    return { success: true, sale: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ════════════════════════════════════════════
// ORGANISATION
// ════════════════════════════════════════════
const ORG = {
  name: process.env.REACT_APP_ORGANISATION_NAME || 'ACCE Tuckshop',
  address: process.env.REACT_APP_RESTAURANT_ADDRESS || '',
  phone: process.env.REACT_APP_RESTAURANT_PHONE || '',
  email: process.env.REACT_APP_RESTAURANT_EMAIL || '',
  website: process.env.REACT_APP_RESTAURANT_WEBSITE || '',
  city: process.env.REACT_APP_RESTAURANT_CITY || '',
  state: process.env.REACT_APP_RESTAURANT_STATE || '',
  country: process.env.REACT_APP_RESTAURANT_COUNTRY || '',
  hours: process.env.REACT_APP_RESTAURANT_OPERATING_HOURS || '',
  supportEmail: process.env.REACT_APP_RESTAURANT_SUPPORT_EMAIL || '',
  supportPhone: process.env.REACT_APP_RESTAURANT_SUPPORT_PHONE || '',
};

const TUCKSHOP = {
  name: 'School Tuckshop',
  icon: '🏪',
  color: ACCE_BRAND.orange,
  darkColor: ACCE_BRAND.orangeDark,
  lightColor: ACCE_BRAND.orangeLight,
};

const PAYMENT_METHOD_CONFIG = {
  cash:         { label: 'Cash',         color: '#2E7D32', bg: '#E8F5E9' },
  card:         { label: 'Card',         color: '#1565C0', bg: '#E3F2FD' },
  transfer:     { label: 'Transfer',     color: '#6A1B9A', bg: '#F3E5F5' },
  pos:          { label: 'POS',          color: '#EF6C00', bg: '#FFF3E0' },
  mixed:        { label: 'Mixed',        color: '#B31B1B', bg: '#FCE4EC' },
  wallet:       { label: 'Wallet',       color: '#00897B', bg: '#E0F2F1' },
  unknown:      { label: 'Unknown',      color: '#9E9E9E', bg: '#F5F5F5' },
};

const ITEMS_PER_PAGE = 10;
const ANALYTICS_LIMIT = 200; // how many recent sales we pull for chart aggregation

// ════════════════════════════════════════════
// UTILITIES
// ════════════════════════════════════════════
const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '₦0.00';
  return `₦${Number(v).toLocaleString('en-NG', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
  });
};

const formatDateTime = (d) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const formatTime = (d) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const formatDateInput = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const formatDateForAPI = (d) => {
  if (!d) return null;
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const getPaymentMethodColor = (m) =>
  PAYMENT_METHOD_CONFIG[String(m || '').toLowerCase()]?.color || '#9E9E9E';
const getPaymentMethodLabel = (m) =>
  PAYMENT_METHOD_CONFIG[String(m || '').toLowerCase()]?.label || m || 'Unknown';

const formatPct = (v) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
};

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const round2 = (n) => Math.round(safeNumber(n) * 100) / 100;

// "Today" range — default filter
const makeTodayRange = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { startDate: start, endDate: end };
};

const describeRange = (dateRange) => {
  const { startDate, endDate } = dateRange || {};
  if (!startDate || !endDate) return 'All time';
  const s = new Date(startDate).toDateString();
  const e = new Date(endDate).toDateString();
  const t = new Date().toDateString();

  if (s === t && e === t) return "Today's";

  const start = new Date(startDate);
  const end = new Date(endDate);
  const diff = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;

  if (diff === 2 && e === t) return "Yesterday + Today's";
  if (diff === 7) return 'Last 7 days';
  if (diff === 30) return 'Last 30 days';
  if (diff === 90) return 'Last 90 days';
  return `${formatDate(startDate)} → ${formatDate(endDate)}`;
};

// Get a human-readable display name for the payer on any given sale
const getSalePayerDisplay = (sale) => {
  if (!sale) return null;
  if (sale.customer_name) {
    return { name: sale.customer_name, sub: sale.customer_phone || null, kind: 'customer' };
  }
  if (sale.payer_name || sale.payer_staff_id) {
    const sub = sale.payer_staff_id ? `Student: ${sale.payer_staff_id}` : null;
    return { name: sale.payer_name || 'Wallet Payer', sub, kind: 'payer' };
  }
  return null;
};

// ════════════════════════════════════════════
// CHART STYLING
// ════════════════════════════════════════════
const chartCardSx = (color) => ({
  borderRadius: '16px',
  border: `1px solid ${alpha(color, 0.08)}`,
  height: '100%',
  minHeight: 300,
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: `0 8px 32px ${alpha(color, 0.1)}`,
    borderColor: alpha(color, 0.2),
  },
});

const ChartHeader = ({ title, color }) => (
  <Typography
    variant="h6" fontWeight={700}
    sx={{
      mb: 2.5, color, fontFamily: '"Inter", sans-serif',
      fontSize: { xs: '0.9rem', sm: '1.05rem' },
      display: 'flex', alignItems: 'center', gap: 1,
    }}
  >
    <Box sx={{ width: 4, height: 20, bgcolor: color, borderRadius: 2 }} />
    {title}
  </Typography>
);

const baseTooltip = (color) => ({
  backgroundColor: 'rgba(255,255,255,0.96)',
  titleColor: '#1a1a1a',
  bodyColor: '#333',
  borderColor: alpha(color, 0.2),
  borderWidth: 1,
  cornerRadius: 12,
  padding: 12,
  titleFont: { weight: '700', family: '"Inter", sans-serif' },
  bodyFont: { weight: '600', family: '"Inter", sans-serif' },
});

const baseLegend = (isMobile, position = 'bottom') => ({
  position: isMobile ? 'bottom' : position,
  labels: {
    font: { size: isMobile ? 10 : 12, weight: '700', family: '"Inter", sans-serif' },
    usePointStyle: true,
    pointStyle: 'circle',
    padding: 14,
  },
});

// ════════════════════════════════════════════
// CHART COMPONENTS
// ════════════════════════════════════════════
const RevenueLineChart = memo(({ data, color, title, height = 280 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const options = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: baseTooltip(color) },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: alpha('#000', 0.06), drawBorder: false },
        ticks: {
          font: { size: isMobile ? 9 : 11, weight: '600', family: '"Inter", sans-serif' },
          color: '#666',
          callback: (v) => '₦' + Number(v).toLocaleString(),
        },
      },
      x: {
        grid: { display: false },
        ticks: {
          font: { size: isMobile ? 8 : 10, weight: '600', family: '"Inter", sans-serif' },
          color: '#666', maxRotation: isMobile ? 45 : 0,
        },
      },
    },
  }), [isMobile, color]);
  return (
    <Card elevation={0} sx={chartCardSx(color)}>
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.3)})` }} />
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <ChartHeader title={title} color={color} />
        <Box sx={{ height: isMobile ? 200 : height }}>
          <Line data={data} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
});

const CategoryBarChart = memo(({ data, color, title, height = 280 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const options = useMemo(() => ({
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...baseTooltip(color),
        callbacks: { label: (ctx) => '₦' + Number(ctx.parsed.x).toLocaleString() },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: alpha('#000', 0.06) },
        ticks: {
          font: { size: isMobile ? 8 : 10, weight: '600', family: '"Inter", sans-serif' },
          callback: (v) => '₦' + Number(v).toLocaleString(),
        },
      },
      y: {
        grid: { display: false },
        ticks: {
          font: { size: isMobile ? 9 : 11, weight: '700', family: '"Inter", sans-serif' },
          color: '#444',
        },
      },
    },
  }), [isMobile, color]);
  return (
    <Card elevation={0} sx={chartCardSx(color)}>
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.3)})` }} />
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <ChartHeader title={title} color={color} />
        <Box sx={{ height: isMobile ? 220 : height }}>
          <Bar data={data} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
});

const PaymentPolarChart = memo(({ data, color, title, height = 280 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const options = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: baseLegend(isMobile, 'right'),
      tooltip: baseTooltip(color),
    },
    scales: {
      r: {
        grid: { color: alpha('#000', 0.06) },
        ticks: { font: { size: 10, weight: '600', family: '"Inter", sans-serif' } },
        pointLabels: { font: { size: isMobile ? 10 : 12, weight: '700', family: '"Inter", sans-serif' } },
      },
    },
  }), [isMobile, color]);
  return (
    <Card elevation={0} sx={chartCardSx(color)}>
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.3)})` }} />
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <ChartHeader title={title} color={color} />
        <Box sx={{ height: isMobile ? 220 : height }}>
          <PolarArea data={data} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
});

const HourlyRadarChart = memo(({ data, color, title, height = 280 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const options = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: baseTooltip(color) },
    scales: {
      r: {
        min: 0,
        grid: { color: alpha('#000', 0.08) },
        ticks: { font: { size: 9, weight: '600', family: '"Inter", sans-serif' } },
        pointLabels: { font: { size: isMobile ? 8 : 10, weight: '700', family: '"Inter", sans-serif' } },
      },
    },
  }), [isMobile, color]);
  return (
    <Card elevation={0} sx={chartCardSx(color)}>
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.3)})` }} />
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <ChartHeader title={title} color={color} />
        <Box sx={{ height: isMobile ? 220 : height }}>
          <Radar data={data} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
});

// ════════════════════════════════════════════
// KPI CARD
// ════════════════════════════════════════════
const KpiCard = memo(({ label, value, icon, color, change }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '16px',
      border: `1px solid ${alpha(color, 0.12)}`,
      background: `linear-gradient(135deg, ${alpha(color, 0.04)}, ${alpha(color, 0.1)})`,
      height: '100%', position: 'relative', overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
      '&:hover': {
        transform: 'translateY(-6px)',
        boxShadow: `0 12px 40px ${alpha(color, 0.15)}`,
        borderColor: color,
      },
    }}
  >
    <Box sx={{
      position: 'absolute', top: -40, right: -40, width: 120, height: 120,
      borderRadius: '50%',
      background: `radial-gradient(circle, ${alpha(color, 0.08)} 0%, transparent 70%)`,
      pointerEvents: 'none',
    }} />
    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{
          bgcolor: alpha(color, 0.12), color,
          width: { xs: 40, sm: 50 }, height: { xs: 40, sm: 50 },
          border: `2px solid ${alpha(color, 0.15)}`,
        }}>
          {icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800}
            sx={{
              fontSize: { xs: '1rem', sm: '1.3rem' },
              color, lineHeight: 1.1, fontFamily: '"Inter", sans-serif',
            }}>
            {value}
          </Typography>
          <Typography variant="caption"
            sx={{
              color: 'text.secondary', fontWeight: 700,
              fontSize: { xs: '0.6rem', sm: '0.7rem' },
              fontFamily: '"Inter", sans-serif',
              textTransform: 'uppercase', letterSpacing: '0.3px',
            }}>
            {label}
          </Typography>
          {change !== undefined && change !== null && Number.isFinite(change) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.4 }}>
              {change >= 0
                ? <TrendingUpIcon sx={{ fontSize: 13, color: '#2E7D32' }} />
                : <TrendingDownIcon sx={{ fontSize: 13, color: '#D32F2F' }} />}
              <Typography variant="caption"
                sx={{
                  fontWeight: 800,
                  color: change >= 0 ? '#2E7D32' : '#D32F2F',
                  fontSize: '0.68rem', fontFamily: '"Inter", sans-serif',
                }}>
                {formatPct(change)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
));

// ════════════════════════════════════════════
// DATE RANGE PICKER
// ════════════════════════════════════════════
const DateRangePicker = memo(({ dateRange, onChange, color, darkColor }) => {
  const [open, setOpen] = useState(false);
  const [tempStart, setTempStart] = useState(dateRange.startDate);
  const [tempEnd, setTempEnd] = useState(dateRange.endDate);

  useEffect(() => {
    setTempStart(dateRange.startDate);
    setTempEnd(dateRange.endDate);
  }, [dateRange]);

  const quickRange = (days) => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (days === 0) {
      onChange({ startDate: start, endDate: end });
      setOpen(false);
      return;
    }
    if (days === 'month') {
      start.setDate(1);
    } else if (days === 'yesterday') {
      start.setDate(end.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() - 1);
      end.setHours(23, 59, 59, 999);
    } else {
      start.setDate(end.getDate() - days);
    }
    start.setHours(0, 0, 0, 0);
    onChange({ startDate: start, endDate: end });
    setOpen(false);
  };

  const hasDates = dateRange.startDate && dateRange.endDate;
  const isToday =
    hasDates &&
    new Date(dateRange.startDate).toDateString() === new Date().toDateString() &&
    new Date(dateRange.endDate).toDateString() === new Date().toDateString();

  const quick = [
    { label: 'Today',       value: 0,         icon: <TodayIcon sx={{ fontSize: 15 }} /> },
    { label: 'Yesterday',   value: 'yesterday' },
    { label: 'Last 7 days', value: 7 },
    { label: 'Last 30 days', value: 30 },
    { label: 'This Month',  value: 'month' },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant={hasDates ? 'contained' : 'outlined'}
          size="medium"
          onClick={() => setOpen(true)}
          startIcon={<DateRangeIcon />}
          sx={{
            borderRadius: '10px', textTransform: 'none', fontWeight: 700,
            fontFamily: '"Inter", sans-serif',
            ...(hasDates && { bgcolor: color, '&:hover': { bgcolor: darkColor } }),
            ...(!hasDates && { borderColor: alpha(color, 0.3), color }),
          }}
        >
          {hasDates
            ? isToday
              ? 'Today'
              : `${formatDate(dateRange.startDate)} → ${formatDate(dateRange.endDate)}`
            : 'Select date range'}
        </Button>
        {hasDates && (
          <IconButton size="small" onClick={() => onChange({ startDate: null, endDate: null })}
            sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1, maxWidth: 480 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700} fontFamily='"Inter", sans-serif'>
            Date range
          </Typography>
          <IconButton onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography variant="subtitle2" fontWeight={700}
            sx={{ mb: 1.5, color: 'text.secondary', fontFamily: '"Inter", sans-serif' }}>
            Quick select
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {quick.map((q) => (
              <Button
                key={q.label} size="small" variant="outlined"
                startIcon={q.icon}
                onClick={() => quickRange(q.value)}
                sx={{
                  borderRadius: '8px', textTransform: 'none', fontWeight: 700,
                  fontSize: '0.75rem', fontFamily: '"Inter", sans-serif',
                  borderColor: alpha(color, 0.25), '&:hover': { borderColor: color },
                }}
              >
                {q.label}
              </Button>
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={700}
            sx={{ mb: 2, color: 'text.secondary', fontFamily: '"Inter", sans-serif' }}>
            Custom range
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              label="Start" type="date" size="small" fullWidth
              value={tempStart ? formatDateInput(tempStart) : ''}
              onChange={(e) => setTempStart(e.target.value ? new Date(e.target.value + 'T00:00:00') : null)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              label="End" type="date" size="small" fullWidth
              value={tempEnd ? formatDateInput(tempEnd) : ''}
              onChange={(e) => setTempEnd(e.target.value ? new Date(e.target.value + 'T23:59:59') : null)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={() => {
              setTempStart(null); setTempEnd(null);
              onChange({ startDate: null, endDate: null });
              setOpen(false);
            }}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontFamily: '"Inter", sans-serif' }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            disabled={tempStart && tempEnd && tempStart > tempEnd}
            onClick={() => {
              onChange({ startDate: tempStart, endDate: tempEnd });
              setOpen(false);
            }}
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 700,
              fontFamily: '"Inter", sans-serif',
              bgcolor: color, '&:hover': { bgcolor: darkColor },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

// ════════════════════════════════════════════
// RECEIPT (thermal 80mm)
// ════════════════════════════════════════════
const RECEIPT_CSS = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0; background: #fff; color: #000;
    font-family: 'Courier New', 'Courier', monospace;
    font-size: 11px; line-height: 1.28;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .r-wrap { width: 300px; margin: 0 auto; padding: 4px 6px 6px 6px; }
  .r-center { text-align: center; }
  .r-bold { font-weight: 700; }
  .r-brand { font-size: 15px; font-weight: 800; letter-spacing: 0.6px; }
  .r-muted { color: #222; }
  .r-sep, .r-sep-d { border: 0; border-top: 1px dashed #000; margin: 4px 0; }
  .r-row { display: flex; justify-content: space-between; gap: 6px; }
  .r-sku { font-size: 9.5px; }
  .r-grand { display: flex; justify-content: space-between; font-size: 14px; font-weight: 800; margin-top: 2px; }
  table.r-table { width: 100%; border-collapse: collapse; }
  table.r-table th, table.r-table td { padding: 1px 0; vertical-align: top; font-size: 10.5px; }
  table.r-table th { border-bottom: 1px dashed #000; font-weight: 700; }
  .r-th-qty { text-align: left; width: 55%; }
  .r-th-disc { text-align: right; width: 18%; }
  .r-th-tot { text-align: right; width: 27%; }
  @media print { @page { size: 80mm auto; margin: 0; } html, body { width: 80mm; } .r-wrap { width: 80mm; padding: 2mm 3mm 3mm 3mm; } }
`;

const buildReceiptHtml = (sale, locationName) => {
  const safe = (v) => (v == null ? '' : String(v));
  const esc = (v) => safe(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const fmt = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '0.00';
    return n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const methodLabel = getPaymentMethodLabel(sale.payment_method);

  let mixedRowsHtml = '';
  if (sale.payment_method === 'mixed' && Array.isArray(sale.payment_breakdown)) {
    mixedRowsHtml = sale.payment_breakdown.map((b) => `
      <div class="r-row r-muted">
        <span>• ${esc(getPaymentMethodLabel(b.channel))}</span>
        <span>${esc(fmt(b.amount))}</span>
      </div>
    `).join('');
  }

  const itemsHtml = (sale.items || []).map((it) => {
    const sku = it.product_code || it.product_barcode || '';
    const discount = Number(it.line_discount) || 0;
    return `
      <tr><td class="r-th-qty" colspan="3">
        <div class="r-bold">${esc(it.product_name)}</div>
        ${sku ? `<div class="r-sku r-muted">SKU: ${esc(sku)}</div>` : ''}
      </td></tr>
      <tr>
        <td class="r-th-qty">${esc(it.quantity)} × ${esc(fmt(it.unit_price))}</td>
        <td class="r-th-disc">${discount > 0 ? '-' + esc(fmt(discount)) : ''}</td>
        <td class="r-th-tot r-bold">${esc(fmt(it.line_total))}</td>
      </tr>
    `;
  }).join('');

  const orgAddressLine = [ORG.address, ORG.city, ORG.state].filter(Boolean).join(', ');
  const payer = getSalePayerDisplay(sale);

  return `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Receipt ${esc(sale.reference)}</title><style>${RECEIPT_CSS}</style></head><body>
    <div class="r-wrap">
      <div class="r-center r-brand">${esc(ORG.name)}</div>
      ${orgAddressLine ? `<div class="r-center r-muted">${esc(orgAddressLine)}</div>` : ''}
      ${ORG.phone ? `<div class="r-center r-muted">Tel: ${esc(ORG.phone)}</div>` : ''}
      <hr class="r-sep" />
      <div class="r-row"><span class="r-bold">STORE</span><span>${esc(locationName)}</span></div>
      <div class="r-row"><span class="r-bold">RECEIPT</span><span>${esc(sale.reference)}</span></div>
      <div class="r-row"><span class="r-bold">DATE</span><span>${esc(new Date(sale.sold_at || sale.created_at).toLocaleString('en-NG'))}</span></div>
      ${sale.cashier_name ? `<div class="r-row"><span class="r-bold">CASHIER</span><span>${esc(sale.cashier_name)}</span></div>` : ''}
      ${payer ? `<div class="r-row"><span class="r-bold">${payer.kind === 'customer' ? 'CUSTOMER' : 'PAYER'}</span><span>${esc(payer.name)}</span></div>` : ''}
      ${payer && payer.sub ? `<div class="r-row"><span class="r-bold">${payer.kind === 'customer' ? 'PHONE' : 'STUDENT ID'}</span><span>${esc(payer.sub)}</span></div>` : ''}
      <hr class="r-sep" />
      <table class="r-table">
        <thead><tr><th class="r-th-qty">Item</th><th class="r-th-disc">Disc</th><th class="r-th-tot">Amount</th></tr></thead>
        <tbody>${itemsHtml || '<tr><td colspan="3" class="r-center r-muted">No items</td></tr>'}</tbody>
      </table>
      <hr class="r-sep" />
      <div class="r-row"><span>Subtotal</span><span>${esc(fmt(sale.subtotal))}</span></div>
      <div class="r-row"><span>Discount</span><span>-${esc(fmt(sale.discount_total))}</span></div>
      <div class="r-row"><span>Tax</span><span>${esc(fmt(sale.tax_total))}</span></div>
      <hr class="r-sep-d" />
      <div class="r-grand"><span>TOTAL</span><span>₦${esc(fmt(sale.total))}</span></div>
      <hr class="r-sep" />
      <div class="r-row"><span class="r-bold">PAYMENT</span><span>${esc(methodLabel)}</span></div>
      <div class="r-row"><span>Tendered</span><span>${esc(fmt(sale.amount_tendered))}</span></div>
      <div class="r-row"><span>Change</span><span>${esc(fmt(sale.change_given))}</span></div>
      ${typeof sale.wallet_balance_after === 'number' ? `<div class="r-row"><span>New balance</span><span>₦${esc(fmt(sale.wallet_balance_after))}</span></div>` : ''}
      ${sale.wallet_reference ? `<div class="r-row r-muted"><span>Wallet ref</span><span>${esc(sale.wallet_reference)}</span></div>` : ''}
      ${mixedRowsHtml ? `<hr class="r-sep-d" /><div class="r-bold">Breakdown</div>${mixedRowsHtml}` : ''}
      ${sale.note ? `<hr class="r-sep-d" /><div class="r-bold">Note</div><div class="r-muted">${esc(sale.note)}</div>` : ''}
      <hr class="r-sep" />
      <div class="r-center r-bold">Thank you for your patronage!</div>
      ${ORG.hours ? `<div class="r-center r-muted">Hours: ${esc(ORG.hours)}</div>` : ''}
      ${ORG.supportPhone ? `<div class="r-center r-muted">Support: ${esc(ORG.supportPhone)}</div>` : ''}
      <div class="r-center r-muted r-sku">*** END OF RECEIPT ***</div>
    </div></body></html>`;
};

// ════════════════════════════════════════════
// AGGREGATION — build analytics from a raw sales array
// ════════════════════════════════════════════
const buildAnalytics = (sales) => {
  const list = Array.isArray(sales) ? sales : [];

  let totalRevenue = 0;
  let totalDiscount = 0;
  let totalTax = 0;
  let totalItems = 0;

  const payAgg = {};
  const catAgg = {};
  const prodAgg = {};
  const hourAgg = Array.from({ length: 24 }, (_, h) => ({ hour: h, label: `${String(h).padStart(2, '0')}:00`, sales_count: 0, revenue: 0 }));
  const dayAgg = {};
  const customerSet = new Set();
  const cashierAgg = {};

  list.forEach((s) => {
    const total = safeNumber(s.total);
    totalRevenue += total;
    totalDiscount += safeNumber(s.discount_total);
    totalTax += safeNumber(s.tax_total);

    // Payment aggregation
    const pm = String(s.payment_method || 'unknown').toLowerCase();
    if (!payAgg[pm]) payAgg[pm] = { method: pm, revenue: 0, count: 0 };
    payAgg[pm].revenue += total;
    payAgg[pm].count += 1;

    // Cashier aggregation
    const cashier = s.cashier_name || s.cashier_email || 'Unknown';
    if (!cashierAgg[cashier]) cashierAgg[cashier] = { cashier_name: cashier, sales_count: 0, items_sold: 0, revenue: 0 };
    cashierAgg[cashier].sales_count += 1;
    cashierAgg[cashier].revenue += total;

    // Hourly aggregation
    const soldAt = s.sold_at || s.created_at;
    if (soldAt) {
      const h = new Date(soldAt).getHours();
      if (hourAgg[h]) {
        hourAgg[h].sales_count += 1;
        hourAgg[h].revenue += total;
      }
      // Day aggregation (for trend)
      const dayKey = new Date(soldAt).toISOString().split('T')[0];
      if (!dayAgg[dayKey]) dayAgg[dayKey] = 0;
      dayAgg[dayKey] += total;
    }

    // Customer — count both phone-bearing customers and wallet payers
    if (s.customer_phone) customerSet.add(s.customer_phone);
    else if (s.payer_staff_id) customerSet.add(`student:${s.payer_staff_id}`);

    // Items aggregation
    (s.items || []).forEach((it) => {
      const qty = safeNumber(it.quantity);
      const lineTotal = safeNumber(it.line_total);
      totalItems += qty;
      cashierAgg[cashier].items_sold += qty;

      // Product aggregation
      const pid = it.product_id;
      if (!prodAgg[pid]) prodAgg[pid] = {
        product_id: pid,
        product_name: it.product_name || 'Unknown',
        product_brand: null,
        quantity_sold: 0,
        revenue: 0,
      };
      prodAgg[pid].quantity_sold += qty;
      prodAgg[pid].revenue += lineTotal;

      // Category aggregation
      const cat = it.product_category || 'Uncategorised';
      if (!catAgg[cat]) catAgg[cat] = { category: cat, revenue: 0, quantity: 0 };
      catAgg[cat].revenue += lineTotal;
      catAgg[cat].quantity += qty;
    });
  });

  // Payment breakdown with percentages
  const paymentTotal = Object.values(payAgg).reduce((a, p) => a + p.revenue, 0);
  const payment_breakdown = Object.values(payAgg)
    .map((p) => ({
      ...p,
      revenue: round2(p.revenue),
      percentage: paymentTotal ? round2((p.revenue / paymentTotal) * 100) : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue);

  // Category breakdown
  const category_breakdown = Object.values(catAgg)
    .map((c) => ({ ...c, revenue: round2(c.revenue) }))
    .sort((a, b) => b.revenue - a.revenue);

  // Top products
  const top_products = Object.values(prodAgg)
    .map((p) => ({ ...p, revenue: round2(p.revenue) }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  // Cashier performance
  const cashier_performance = Object.values(cashierAgg)
    .map((c) => ({
      ...c,
      revenue: round2(c.revenue),
      avg_sale: c.sales_count ? round2(c.revenue / c.sales_count) : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue);

  // Revenue trend — sort by date
  const revenue_trend = Object.entries(dayAgg)
    .map(([day, revenue]) => ({
      label: new Date(day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      revenue: round2(revenue),
    }))
    .sort((a, b) => new Date(a.label) - new Date(b.label));

  // If only one day (today), split by hour instead for a useful chart
  let trend = revenue_trend;
  if (revenue_trend.length <= 1) {
    trend = hourAgg
      .filter((h) => h.sales_count > 0 || (h.hour >= 6 && h.hour <= 20))
      .map((h) => ({ label: h.label, revenue: round2(h.revenue) }));
  }

  return {
    kpi: {
      total_revenue: round2(totalRevenue),
      total_sales: list.length,
      total_items_sold: totalItems,
      unique_customers: customerSet.size,
      total_discount: round2(totalDiscount),
      total_tax: round2(totalTax),
    },
    payment_breakdown,
    category_breakdown,
    top_products,
    cashier_performance,
    revenue_trend: trend,
    hourly_distribution: hourAgg.map(({ hour, label, sales_count }) => ({
      hour, label, sales_count,
    })),
  };
};

// ════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════
const RetailSalesView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── State ──
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [activeMenu, setActiveMenu] = useState('Menu');

  const [sales, setSales] = useState([]);              // paginated (page view)
  const [allSales, setAllSales] = useState([]);        // analytics dataset
  const [salesTotal, setSalesTotal] = useState(0);

  const [dateRange, setDateRange] = useState(makeTodayRange);  // DEFAULT = TODAY
  const [paymentFilter, setPaymentFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchValue, setSearchValue] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [activeTab, setActiveTab] = useState(0);

  const [detailSale, setDetailSale] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isMountedRef = useRef(true);
  const debounceRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const pushSnackbar = useCallback((message, severity = 'success') => {
    if (!isMountedRef.current) return;
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // ════════════════════════════════════════════
  // FETCH: PAGINATED SALES LIST
  // ════════════════════════════════════════════
  const fetchSalesPage = useCallback(async () => {
    if (!isMountedRef.current) return;
    setIsFetching(true);

    const params = {
      page: page + 1,
      page_size: rowsPerPage,
    };
    if (dateRange.startDate) params.from = formatDateForAPI(dateRange.startDate);
    if (dateRange.endDate)   params.to   = formatDateForAPI(dateRange.endDate) + ' 23:59:59';
    if (paymentFilter !== 'ALL') params.payment_method = paymentFilter;
    // Use `search` so backend searches reference + customer + payer + cashier
    if (searchDebounced) params.search = searchDebounced;

    const res = await apiListSales(params);
    if (!isMountedRef.current) return;

    if (res.success) {
      // Apply client-side status filter (backend doesn't support it yet)
      let items = res.data || [];
      if (statusFilter !== 'ALL') {
        items = items.filter((s) =>
          String(s.status || 'completed').toLowerCase() === statusFilter.toLowerCase()
        );
      }
      setSales(items);
      setSalesTotal(res.total || items.length);
    } else {
      pushSnackbar(res.error || 'Failed to load sales', 'error');
      setSales([]);
      setSalesTotal(0);
    }
    setIsFetching(false);
  }, [page, rowsPerPage, dateRange, paymentFilter, statusFilter, searchDebounced, pushSnackbar]);

  // ════════════════════════════════════════════
  // FETCH: ANALYTICS DATASET (larger window)
  // ════════════════════════════════════════════
  const fetchAnalyticsData = useCallback(async () => {
    if (!isMountedRef.current) return;

    const params = {
      page: 1,
      page_size: ANALYTICS_LIMIT,
    };
    if (dateRange.startDate) params.from = formatDateForAPI(dateRange.startDate);
    if (dateRange.endDate)   params.to   = formatDateForAPI(dateRange.endDate) + ' 23:59:59';
    if (paymentFilter !== 'ALL') params.payment_method = paymentFilter;
    // Use `search` for a unified search
    if (searchDebounced) params.search = searchDebounced;

    const res = await apiListSales(params);
    if (!isMountedRef.current) return;

    if (res.success) {
      let items = res.data || [];
      if (statusFilter !== 'ALL') {
        items = items.filter((s) =>
          String(s.status || 'completed').toLowerCase() === statusFilter.toLowerCase()
        );
      }
      setAllSales(items);
    }
  }, [dateRange, paymentFilter, statusFilter, searchDebounced]);

  // ── Effects ──
  useEffect(() => {
    (async () => {
      await Promise.all([fetchSalesPage(), fetchAnalyticsData()]);
      if (isMountedRef.current) setIsLoading(false);
    })();
  }, [fetchSalesPage, fetchAnalyticsData]);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchDebounced(searchValue.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchValue]);

  // ── Derived analytics ──
  const analytics = useMemo(() => buildAnalytics(allSales), [allSales]);
  const kpi = analytics.kpi;

  // ════════════════════════════════════════════
  // CHART DATA
  // ════════════════════════════════════════════
  const revenueChartData = useMemo(() => ({
    labels: analytics.revenue_trend.map((p) => p.label),
    datasets: [{
      label: 'Revenue',
      data: analytics.revenue_trend.map((p) => p.revenue),
      borderColor: TUCKSHOP.color,
      backgroundColor: alpha(TUCKSHOP.color, 0.12),
      borderWidth: 3,
      fill: true,
      tension: 0.4,
      pointBackgroundColor: TUCKSHOP.color,
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 7,
    }],
  }), [analytics.revenue_trend]);

  const categoryChartData = useMemo(() => {
    const palette = [TUCKSHOP.color, '#1565C0', '#2E7D32', '#EF6C00', '#6A1B9A', '#00838F', '#795548'];
    return {
      labels: analytics.category_breakdown.map((c) => c.category),
      datasets: [{
        label: 'Revenue',
        data: analytics.category_breakdown.map((c) => c.revenue),
        backgroundColor: analytics.category_breakdown.map((_, i) => alpha(palette[i % palette.length], 0.75)),
        borderColor: analytics.category_breakdown.map((_, i) => palette[i % palette.length]),
        borderWidth: 2,
        borderRadius: 6,
      }],
    };
  }, [analytics.category_breakdown]);

  const paymentChartData = useMemo(() => ({
    labels: analytics.payment_breakdown.map((p) => getPaymentMethodLabel(p.method)),
    datasets: [{
      data: analytics.payment_breakdown.map((p) => p.revenue),
      backgroundColor: analytics.payment_breakdown.map((p) => alpha(getPaymentMethodColor(p.method), 0.75)),
      borderColor: analytics.payment_breakdown.map((p) => getPaymentMethodColor(p.method)),
      borderWidth: 2,
      hoverOffset: 10,
    }],
  }), [analytics.payment_breakdown]);

  const hourlyChartData = useMemo(() => ({
    labels: analytics.hourly_distribution.filter((h) => h.hour >= 6 && h.hour <= 22).map((h) => h.label),
    datasets: [{
      label: 'Sales',
      data: analytics.hourly_distribution.filter((h) => h.hour >= 6 && h.hour <= 22).map((h) => h.sales_count),
      backgroundColor: alpha(TUCKSHOP.color, 0.4),
      borderColor: TUCKSHOP.color,
      borderWidth: 2,
    }],
  }), [analytics.hourly_distribution]);

  // ════════════════════════════════════════════
  // HANDLERS
  // ════════════════════════════════════════════
  const handleRefresh = useCallback(async () => {
    await Promise.all([fetchSalesPage(), fetchAnalyticsData()]);
    pushSnackbar('Data refreshed', 'success');
  }, [fetchSalesPage, fetchAnalyticsData, pushSnackbar]);

  const handleRowClick = useCallback(async (sale) => {
    setDetailLoading(true);
    const res = await apiGetSale(sale.id);
    if (!isMountedRef.current) return;
    if (res.success) {
      setDetailSale(res.sale);
    } else {
      pushSnackbar(res.error || 'Failed to load sale', 'error');
    }
    setDetailLoading(false);
  }, [pushSnackbar]);

  const handleCloseDetail = useCallback(() => setDetailSale(null), []);

  const handlePrintReceipt = useCallback(() => {
    if (!detailSale) return;
    const html = buildReceiptHtml(detailSale, TUCKSHOP.name);

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(iframe);
      const w = window.open('', '_blank', 'width=340,height=640');
      if (!w) return;
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 250);
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    const trigger = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        setTimeout(() => {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 800);
      }
    };

    if (iframe.contentWindow.document.readyState === 'complete') {
      setTimeout(trigger, 120);
    } else {
      iframe.onload = () => setTimeout(trigger, 120);
    }
  }, [detailSale]);

  const rangeLabel = describeRange(dateRange);
  const rangePrefix = rangeLabel && rangeLabel !== 'All time' ? `${rangeLabel} ` : '';

  // ════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════
  if (isLoading) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <div className="epos-wrapper">
          <div className="epos-main-layout">
            <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
            <div className="epos-main">
              <Header />
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={60} sx={{ color: TUCKSHOP.color }} />
                <Typography variant="h6" sx={{ mt: 2, fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                  Loading sales dashboard...
                </Typography>
              </Box>
            </div>
          </div>
        </div>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
          <div className="epos-main">
            <Header />

            <div className="epos-content" style={{ background: '#f5f7fa' }}>
              {/* ══════════ HEADER ══════════ */}
              <Box
                sx={{
                  bgcolor: 'white', borderRadius: '16px',
                  p: { xs: 1.5, sm: 2.5, md: 3 },
                  mb: 3, border: `1px solid ${alpha(TUCKSHOP.color, 0.15)}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography
                      variant="h4" fontWeight={800}
                      sx={{
                        color: TUCKSHOP.color,
                        fontSize: { xs: '1.15rem', sm: '1.7rem', md: '1.9rem' },
                        fontFamily: '"Inter", sans-serif', letterSpacing: '-0.5px',
                      }}
                    >
                      📊 Sales Dashboard
                    </Typography>
                    <Typography
                      variant="body1" color="text.secondary"
                      sx={{
                        mt: 0.5, fontWeight: 700,
                        fontSize: { xs: '0.8rem', sm: '0.95rem' },
                        fontFamily: '"Inter", sans-serif',
                      }}
                    >
                      {TUCKSHOP.icon} {TUCKSHOP.name}
                      {rangeLabel && (
                        <span style={{ marginLeft: 8, color: TUCKSHOP.color, fontWeight: 700 }}>
                          {' · '}
                          {rangeLabel}
                        </span>
                      )}
                    </Typography>
                  </Box>
                  <Chip
                    icon={<AssessmentIcon />}
                    label="Manager View"
                    variant="outlined"
                    sx={{
                      borderColor: alpha(TUCKSHOP.color, 0.3), color: TUCKSHOP.color,
                      fontWeight: 700,
                      fontSize: { xs: '0.65rem', sm: '0.8rem' },
                      height: { xs: 28, sm: 34 },
                      fontFamily: '"Inter", sans-serif',
                    }}
                  />
                </Box>
              </Box>

              {/* ══════════ FILTER BAR ══════════ */}
              <Box
                sx={{
                  display: 'flex', flexDirection: { xs: 'column', md: 'row' },
                  gap: 2, mb: 3, alignItems: { xs: 'stretch', md: 'center' },
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search reference, customer, student ID..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    sx={{
                      minWidth: 220, flex: 1, maxWidth: { xs: '100%', sm: 340 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px', bgcolor: 'white', fontWeight: 600,
                        '& fieldset': { borderColor: alpha(TUCKSHOP.color, 0.2) },
                        '&:hover fieldset': { borderColor: TUCKSHOP.color },
                        '&.Mui-focused fieldset': { borderColor: TUCKSHOP.color },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#94a3b8' }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchValue ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchValue('')}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />

                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>Payment</InputLabel>
                    <Select
                      value={paymentFilter}
                      label="Payment"
                      onChange={(e) => { setPaymentFilter(e.target.value); setPage(0); }}
                      sx={{
                        borderRadius: '12px', bgcolor: 'white',
                        '& .MuiSelect-select': { fontWeight: 600, fontFamily: '"Inter", sans-serif' },
                      }}
                    >
                      <MenuItem value="ALL" sx={{ fontWeight: 600 }}>All</MenuItem>
                      <MenuItem value="cash" sx={{ fontWeight: 600 }}>Cash</MenuItem>
                      <MenuItem value="card" sx={{ fontWeight: 600 }}>Card</MenuItem>
                      <MenuItem value="transfer" sx={{ fontWeight: 600 }}>Transfer</MenuItem>
                      <MenuItem value="pos" sx={{ fontWeight: 600 }}>POS</MenuItem>
                      <MenuItem value="mixed" sx={{ fontWeight: 600 }}>Mixed</MenuItem>
                      <MenuItem value="wallet" sx={{ fontWeight: 600 }}>Wallet</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel sx={{ fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                      sx={{
                        borderRadius: '12px', bgcolor: 'white',
                        '& .MuiSelect-select': { fontWeight: 600, fontFamily: '"Inter", sans-serif' },
                      }}
                    >
                      <MenuItem value="ALL" sx={{ fontWeight: 600 }}>All</MenuItem>
                      <MenuItem value="completed" sx={{ fontWeight: 600 }}>Completed</MenuItem>
                      <MenuItem value="voided" sx={{ fontWeight: 600 }}>Voided</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <DateRangePicker
                    dateRange={dateRange}
                    onChange={setDateRange}
                    color={TUCKSHOP.color}
                    darkColor={TUCKSHOP.darkColor}
                  />

                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={handleRefresh}
                      disabled={isFetching}
                      sx={{
                        bgcolor: alpha(TUCKSHOP.color, 0.08),
                        '&:hover': { bgcolor: alpha(TUCKSHOP.color, 0.15) },
                        borderRadius: '10px', width: 40, height: 40,
                      }}
                    >
                      {isFetching ? <CircularProgress size={20} /> : <RefreshIcon sx={{ color: TUCKSHOP.color }} />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* ══════════ KPI CARDS ══════════ */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard
                    label={`${rangePrefix}Revenue`}
                    value={formatCurrency(kpi.total_revenue)}
                   
                    color={TUCKSHOP.color}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard
                    label={`${rangePrefix}Sales`}
                    value={kpi.total_sales}
                    icon={<ReceiptIcon />}
                    color={TUCKSHOP.color}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard
                    label={`${rangePrefix}Items Sold`}
                    value={kpi.total_items_sold}
                    icon={<ShoppingBagIcon />}
                    color={TUCKSHOP.color}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard
                    label="Unique Customers"
                    value={kpi.unique_customers}
                    icon={<PeopleIcon />}
                    color={TUCKSHOP.color}
                  />
                </Grid>
              </Grid>

              {/* ══════════ TABS ══════════ */}
              <Box sx={{ mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    bgcolor: 'white', borderRadius: '14px', p: 0.5,
                    border: `1px solid ${alpha(TUCKSHOP.color, 0.08)}`,
                    '& .MuiTabs-indicator': { height: 3, bgcolor: TUCKSHOP.color, borderRadius: '3px' },
                    '& .MuiTab-root': {
                      textTransform: 'none', fontWeight: 700,
                      fontFamily: '"Inter", sans-serif',
                      fontSize: { xs: '0.65rem', sm: '0.85rem' },
                      minHeight: { xs: 36, sm: 46 },
                      px: { xs: 1.5, sm: 2.5 },
                      borderRadius: '10px',
                      '&.Mui-selected': { color: TUCKSHOP.color, bgcolor: alpha(TUCKSHOP.color, 0.06) },
                    },
                  }}
                >
                  <Tab icon={<BarChartIcon />} label="Overview"  iconPosition="start" />
                  <Tab icon={<ShowChartIcon />} label="Revenue"   iconPosition="start" />
                  <Tab icon={<PieChartIcon />} label="Payments"  iconPosition="start" />
                  <Tab icon={<PeopleIcon />}   label="Team"      iconPosition="start" />
                  <Tab icon={<TimelineIcon />} label="Activity"  iconPosition="start" />
                </Tabs>
              </Box>

              {/* ══════════ TAB PANELS ══════════ */}
              {activeTab === 0 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={7}>
                    <RevenueLineChart data={revenueChartData} color={TUCKSHOP.color} title={`${rangePrefix}Revenue trend`} />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <PaymentPolarChart data={paymentChartData} color={TUCKSHOP.color} title="Payments" />
                  </Grid>
                  <Grid item xs={12}>
                    <CategoryBarChart data={categoryChartData} color={TUCKSHOP.color} title="Top categories by revenue" />
                  </Grid>
                </Grid>
              )}

              {activeTab === 1 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12}>
                    <RevenueLineChart data={revenueChartData} color={TUCKSHOP.color} title={`${rangePrefix}Revenue trend`} height={340} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ ...chartCardSx(TUCKSHOP.color), p: 3 }}>
                      <ChartHeader title="Revenue summary" color={TUCKSHOP.color} />
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Total revenue</Typography>
                          <Typography variant="h6" fontWeight={800} color={TUCKSHOP.color} fontFamily='"Inter", sans-serif'>
                            {formatCurrency(kpi.total_revenue)}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Discounts given</Typography>
                          <Typography variant="h6" fontWeight={800} color="#D32F2F" fontFamily='"Inter", sans-serif'>
                            {formatCurrency(kpi.total_discount)}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>VAT collected</Typography>
                          <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
                            {formatCurrency(kpi.total_tax)}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Avg sale value</Typography>
                          <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
                            {formatCurrency(kpi.total_sales ? kpi.total_revenue / kpi.total_sales : 0)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ ...chartCardSx(TUCKSHOP.color), p: 3 }}>
                      <ChartHeader title="Top products" color={TUCKSHOP.color} />
                      <Stack spacing={1.5}>
                        {analytics.top_products.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3, fontFamily: '"Inter", sans-serif' }}>
                            No products sold in this period
                          </Typography>
                        ) : (
                          analytics.top_products.slice(0, 6).map((p, i) => (
                            <Box key={p.product_id}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                  <Chip
                                    label={i + 1} size="small"
                                    sx={{ bgcolor: alpha(TUCKSHOP.color, 0.1), color: TUCKSHOP.color, fontWeight: 800, height: 22, minWidth: 26 }}
                                  />
                                  <Typography variant="body2" fontWeight={700} noWrap fontFamily='"Inter", sans-serif'>
                                    {p.product_name}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" fontWeight={800} color={TUCKSHOP.color} fontFamily='"Inter", sans-serif'>
                                  {formatCurrency(p.revenue)}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600} fontFamily='"Inter", sans-serif'>
                                {p.quantity_sold} sold
                              </Typography>
                              <Divider sx={{ mt: 1 }} />
                            </Box>
                          ))
                        )}
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {activeTab === 2 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={7}>
                    <PaymentPolarChart data={paymentChartData} color={TUCKSHOP.color} title="Payment breakdown" height={320} />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Card elevation={0} sx={{ ...chartCardSx(TUCKSHOP.color), p: 3 }}>
                      <ChartHeader title="Payment summary" color={TUCKSHOP.color} />
                      <Stack spacing={2}>
                        {analytics.payment_breakdown.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3, fontFamily: '"Inter", sans-serif' }}>
                            No payments in this period
                          </Typography>
                        ) : (
                          analytics.payment_breakdown.map((p) => {
                            const c = getPaymentMethodColor(p.method);
                            return (
                              <Box key={p.method}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c }} />
                                    <Typography variant="body2" fontWeight={700} fontFamily='"Inter", sans-serif'>
                                      {getPaymentMethodLabel(p.method)}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" fontWeight={800} fontFamily='"Inter", sans-serif'>
                                    {formatCurrency(p.revenue)}
                                  </Typography>
                                </Box>
                                <Box sx={{ mt: 0.5, height: 6, borderRadius: 3, bgcolor: alpha('#000', 0.06), overflow: 'hidden' }}>
                                  <Box sx={{ height: '100%', borderRadius: 3, bgcolor: c, width: `${p.percentage}%`, transition: 'width 0.5s ease' }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} fontFamily='"Inter", sans-serif'>
                                  {p.count} transactions · {p.percentage.toFixed(1)}%
                                </Typography>
                              </Box>
                            );
                          })
                        )}
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {activeTab === 3 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12}>
                    <Card elevation={0} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
                      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                        <ChartHeader title="Cashier performance" color={TUCKSHOP.color} />
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: alpha(TUCKSHOP.color, 0.04) }}>
                                <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Cashier</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Sales</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Items</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Revenue</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Avg</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {analytics.cashier_performance.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    No cashier activity
                                  </TableCell>
                                </TableRow>
                              ) : analytics.cashier_performance.map((c, i) => (
                                <TableRow key={i} hover>
                                  <TableCell sx={{ fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar sx={{ bgcolor: alpha(TUCKSHOP.color, 0.12), color: TUCKSHOP.color, width: 30, height: 30, fontSize: '0.75rem' }}>
                                        {(c.cashier_name || '?').charAt(0).toUpperCase()}
                                      </Avatar>
                                      {c.cashier_name}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>{c.sales_count}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>{c.items_sold}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 800, color: TUCKSHOP.color }}>{formatCurrency(c.revenue)}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(c.avg_sale)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {activeTab === 4 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={7}>
                    <RevenueLineChart data={revenueChartData} color={TUCKSHOP.color} title={`${rangePrefix}Revenue trend`} height={320} />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <HourlyRadarChart data={hourlyChartData} color={TUCKSHOP.color} title="Hourly sales" height={320} />
                  </Grid>
                </Grid>
              )}

              {/* ══════════ SALES TABLE ══════════ */}
              <Card elevation={0} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, pb: 0 }}>
                  <ChartHeader title={`${rangePrefix}Sales`} color={TUCKSHOP.color} />
                </CardContent>

                {isFetching && sales.length === 0 ? (
                  <Box sx={{ p: 3 }}>
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '10px', mb: 1 }} />
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '10px', mb: 1 }} />
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '10px' }} />
                  </Box>
                ) : sales.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <ReceiptLongIcon sx={{ fontSize: 72, color: 'text.disabled', opacity: 0.3 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 2, fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                      No sales found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontFamily: '"Inter", sans-serif' }}>
                      Try adjusting the filters or date range
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TableContainer>
                      <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha(TUCKSHOP.color, 0.04) }}>
                            <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Reference</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Cashier</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Customer / Payer</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Payment</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sales.map((s, i) => {
                            const pm = getPaymentMethodColor(s.payment_method);
                            const pmLabel = getPaymentMethodLabel(s.payment_method);
                            const statusColor = s.status === 'voided' ? '#D32F2F' : '#2E7D32';
                            const payer = getSalePayerDisplay(s);
                            return (
                              <TableRow
                                key={s.id}
                                hover
                                onClick={() => handleRowClick(s)}
                                sx={{
                                  cursor: 'pointer',
                                  bgcolor: i % 2 === 0 ? 'transparent' : alpha('#000', 0.015),
                                  '&:hover': { bgcolor: alpha(TUCKSHOP.color, 0.04) },
                                }}
                              >
                                <TableCell>
                                  <Typography variant="body2" fontWeight={700} sx={{ color: TUCKSHOP.color, fontFamily: '"Inter", sans-serif' }}>
                                    {s.reference}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontWeight={600} fontFamily='"Inter", sans-serif'>
                                    {formatTime(s.sold_at || s.created_at)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                                  {s.cashier_name || '—'}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontFamily: '"Inter", sans-serif' }}>
                                  {payer ? (
                                    <>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                        {payer.kind === 'payer' && (
                                          <WalletIcon sx={{ fontSize: 14, color: getPaymentMethodColor('wallet') }} />
                                        )}
                                        {payer.kind === 'customer' && (
                                          <PersonIcon sx={{ fontSize: 14, color: '#64748B' }} />
                                        )}
                                        <Typography variant="body2" fontWeight={700} fontFamily='"Inter", sans-serif' noWrap>
                                          {payer.name}
                                        </Typography>
                                      </Box>
                                      {payer.sub && (
                                        <Typography variant="caption" display="block" color="text.secondary" fontFamily='"Inter", sans-serif'>
                                          {payer.sub}
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" fontFamily='"Inter", sans-serif'>
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={pmLabel}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(pm, 0.12), color: pm,
                                      fontWeight: 700, fontFamily: '"Inter", sans-serif',
                                      border: `1px solid ${alpha(pm, 0.2)}`,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={s.status || 'completed'}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(statusColor, 0.12), color: statusColor,
                                      fontWeight: 700, fontFamily: '"Inter", sans-serif',
                                      border: `1px solid ${alpha(statusColor, 0.2)}`,
                                      textTransform: 'capitalize',
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={800} color={TUCKSHOP.color} fontFamily='"Inter", sans-serif'>
                                    {formatCurrency(s.total)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={salesTotal}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={(_, p) => setPage(p)}
                      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                      sx={{
                        borderTop: `1px solid ${alpha('#000', 0.06)}`,
                        '& .MuiTablePagination-select, & .MuiTablePagination-displayedRows': {
                          fontWeight: 700, fontFamily: '"Inter", sans-serif',
                          fontSize: { xs: '0.7rem', sm: '0.85rem' },
                        },
                      }}
                    />
                  </>
                )}
              </Card>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ══════════ SALE DETAIL DIALOG ══════════ */}
      <Dialog
        open={Boolean(detailSale) || detailLoading}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', maxHeight: '90vh' } }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha('#000', 0.06)}`,
            bgcolor: '#f8f9fa', borderRadius: '20px 20px 0 0',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <ReceiptIcon sx={{ color: TUCKSHOP.color, fontSize: 26 }} />
            <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
              {detailSale?.reference || 'Loading...'}
            </Typography>
            {detailSale && (
              <>
                <Chip
                  size="small"
                  label={detailSale.status || 'completed'}
                  sx={{
                    bgcolor: alpha('#2E7D32', 0.12), color: '#2E7D32',
                    fontWeight: 700, fontFamily: '"Inter", sans-serif', textTransform: 'capitalize',
                  }}
                />
                <Chip
                  size="small"
                  label={getPaymentMethodLabel(detailSale.payment_method)}
                  sx={{
                    bgcolor: alpha(getPaymentMethodColor(detailSale.payment_method), 0.12),
                    color: getPaymentMethodColor(detailSale.payment_method),
                    fontWeight: 700, fontFamily: '"Inter", sans-serif',
                  }}
                />
              </>
            )}
          </Box>
          <IconButton onClick={handleCloseDetail}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {detailLoading && !detailSale ? (
            <Box sx={{ p: 3 }}>
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '10px', mb: 2 }} />
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '10px' }} />
            </Box>
          ) : detailSale ? (
            <>
              <Box
                sx={{
                  p: 3, bgcolor: alpha(TUCKSHOP.color, 0.02),
                  borderBottom: `1px solid ${alpha('#000', 0.06)}`,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Cashier</Typography>
                  <Typography fontWeight={800} fontFamily='"Inter", sans-serif'>{detailSale.cashier_name || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Date</Typography>
                  <Typography fontWeight={800} fontFamily='"Inter", sans-serif'>
                    {formatDateTime(detailSale.sold_at || detailSale.created_at)}
                  </Typography>
                </Box>
                {detailSale.customer_name && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Customer</Typography>
                    <Typography fontWeight={800} fontFamily='"Inter", sans-serif'>
                      {detailSale.customer_name}
                      {detailSale.customer_phone && ` (${detailSale.customer_phone})`}
                    </Typography>
                  </Box>
                )}
                {(detailSale.payer_name || detailSale.payer_staff_id) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>
                      Wallet Payer
                    </Typography>
                    <Typography fontWeight={800} fontFamily='"Inter", sans-serif'>
                      {detailSale.payer_name || '—'}
                      {detailSale.payer_staff_id && ` (${detailSale.payer_staff_id})`}
                    </Typography>
                  </Box>
                )}
                {typeof detailSale.wallet_balance_after === 'number' && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>
                      Wallet balance after
                    </Typography>
                    <Typography fontWeight={800} color={getPaymentMethodColor('wallet')} fontFamily='"Inter", sans-serif'>
                      {formatCurrency(detailSale.wallet_balance_after)}
                    </Typography>
                  </Box>
                )}
                {detailSale.wallet_reference && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>
                      Wallet reference
                    </Typography>
                    <Typography fontWeight={800} fontFamily='"Inter", sans-serif' sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {detailSale.wallet_reference}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={800} mb={2} fontFamily='"Inter", sans-serif'>
                  Items ({(detailSale.items || []).length})
                </Typography>

                <Box sx={{ bgcolor: alpha('#000', 0.02), borderRadius: '12px', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      display: 'grid', gridTemplateColumns: '1fr 70px 110px',
                      gap: 1, p: 2, bgcolor: alpha('#000', 0.04),
                      borderBottom: `1px solid ${alpha('#000', 0.06)}`,
                    }}
                  >
                    <Typography variant="caption" fontWeight={800} fontFamily='"Inter", sans-serif'>Product</Typography>
                    <Typography variant="caption" fontWeight={800} align="center" fontFamily='"Inter", sans-serif'>Qty</Typography>
                    <Typography variant="caption" fontWeight={800} align="right" fontFamily='"Inter", sans-serif'>Amount</Typography>
                  </Box>
                  {(detailSale.items || []).map((it, idx) => (
                    <Box
                      key={it.id ?? idx}
                      sx={{
                        display: 'grid', gridTemplateColumns: '1fr 70px 110px',
                        gap: 1, p: 2, alignItems: 'center',
                        borderBottom: idx < (detailSale.items || []).length - 1 ? `1px solid ${alpha('#000', 0.04)}` : 'none',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} fontFamily='"Inter", sans-serif' noWrap>
                          {it.product_name}
                        </Typography>
                        {(it.product_brand || it.product_category) && (
                          <Typography variant="caption" color="text.secondary" fontWeight={600} fontFamily='"Inter", sans-serif'>
                            {[it.product_brand, it.product_category].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" fontWeight={700} align="center" fontFamily='"Inter", sans-serif'>
                        ×{it.quantity}
                      </Typography>
                      <Typography variant="body2" fontWeight={800} align="right" color={TUCKSHOP.color} fontFamily='"Inter", sans-serif'>
                        {formatCurrency(it.line_total)}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.6 }}>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Subtotal</Typography>
                    <Typography variant="body2" fontWeight={800} fontFamily='"Inter", sans-serif'>
                      {formatCurrency(detailSale.subtotal)}
                    </Typography>
                  </Box>
                  {Number(detailSale.discount_total) > 0 && (
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Discount</Typography>
                      <Typography variant="body2" fontWeight={800} color="#D32F2F" fontFamily='"Inter", sans-serif'>
                        -{formatCurrency(detailSale.discount_total)}
                      </Typography>
                    </Box>
                  )}
                  {Number(detailSale.tax_total) > 0 && (
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>VAT</Typography>
                      <Typography variant="body2" fontWeight={800} fontFamily='"Inter", sans-serif'>
                        {formatCurrency(detailSale.tax_total)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ width: 200, my: 0.5 }} />
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>Total</Typography>
                    <Typography variant="h6" fontWeight={800} color={TUCKSHOP.color} fontFamily='"Inter", sans-serif'>
                      {formatCurrency(detailSale.total)}
                    </Typography>
                  </Box>
                  {detailSale.payment_method === 'mixed' && Array.isArray(detailSale.payment_breakdown) && (
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: alpha(TUCKSHOP.color, 0.04), borderRadius: '10px', minWidth: 240 }}>
                      <Typography variant="caption" fontWeight={800} fontFamily='"Inter", sans-serif'>
                        Split payment
                      </Typography>
                      {detailSale.payment_breakdown.map((b, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography variant="caption" fontWeight={700} fontFamily='"Inter", sans-serif'>
                            {getPaymentMethodLabel(b.channel)}
                          </Typography>
                          <Typography variant="caption" fontWeight={800} fontFamily='"Inter", sans-serif'>
                            {formatCurrency(b.amount)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha('#000', 0.06)}`, gap: 1 }}>
          <Button
            onClick={handleCloseDetail}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontFamily: '"Inter", sans-serif' }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrintReceipt}
            disabled={!detailSale}
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 700,
              fontFamily: '"Inter", sans-serif',
              bgcolor: TUCKSHOP.color, '&:hover': { bgcolor: TUCKSHOP.darkColor },
            }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════ SNACKBAR ══════════ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: '10px', fontWeight: 700, fontFamily: '"Inter", sans-serif' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Suspense>
  );
};

export default RetailSalesView;